import _ from 'lodash';
import useVisibility from '../../hooks/useVisibility';
import container from '@libs/react-easy-form/container';
import useControlState from '../../hooks/useControlState';
import ControllerViewFactory from '../../controller-views';
import { buildEventBusEffect } from '@utilities/event-bus';
import useHelperText from '../../hooks/useHelperText/index';

export const validateEvent = formIndex => `${formIndex}:validate-controller`;

export default function CommonInputAdapter(Input) {
  return function Adapteed(props) {
    const { defaultHelperText, validationRules, defaultValue, name, formIndex } = props;
    const bus = container.getEventBus(formIndex);
    const useBusEffectOn = buildEventBusEffect(bus);

    function validate({ value, validationRules, name, formIndex }) {
      if (validationRules) {
        bus.dispatch(validateEvent(formIndex), { name, value, rules: validationRules });
      }
    }

    // States

    const state = useControlState({
      name,
      formIndex,
      defaultValue,
      useBusEffectOn,
      dispatch: bus.dispatch,
      hook: value => validate({ value, validationRules, name, formIndex }),
    });

    const { helperText } = useHelperText({ name, formIndex, defaultHelperText, useBusEffectOn });

    // Render

    const restProps = _.omit(props, [
      'value',
      'error',
      'onChange',
      'disabled',
      'helperText',
      'formIndex',
      'customView',
      'responsive',
      'defaultValue',
      'validationRules',
      'defaultViewMode',
    ]);

    return (
      <EnsureVisibility props={props} value={state.value} useBusEffectOn={useBusEffectOn} defaultValue={defaultValue}>
        <Input {...restProps} name={name} value={state.value} error={helperText.error} helperText={helperText.message} onChange={state.handleOnChange} />
      </EnsureVisibility>
    );
  };
}

function EnsureVisibility({ props, children, useBusEffectOn, value, defaultValue }) {
  const { name, formIndex, defaultViewMode, noViewRequired, label } = props;
  const visibility = useVisibility({ formIndex, name, defaultViewMode, useBusEffectOn });

  if (noViewRequired) return children;

  if (NoNameIn(props)) return <div aria-label="no-name-provided">No name provided</div>;

  if (NoTypeIn(props)) return <div aria-label="no-type-provided">No type provided</div>;

  if (IsInViewMode(visibility).andCustomHandlerIn(props)) return props.customView(value);

  if (IsInViewMode(visibility).andNoCustomHandlerIn(props)) {
    const state = ensureDefaultValue(value, defaultValue);

    return <ControllerViewFactory label={label} type={props.type} name={name} state={state} />;
  }

  return children;
}

const NoTypeIn = props => !('type' in props);
const NoNameIn = props => !('name' in props);
const IsInViewMode = visibility => {
  return {
    andCustomHandlerIn: props => {
      const hasCustomView = 'customView' in props;
      return visibility.isViewMode && hasCustomView;
    },
    andNoCustomHandlerIn: props => {
      const hasCustomView = 'customView' in props;
      return visibility.isViewMode && !hasCustomView;
    },
  };
};

function ensureDefaultValue(value, defaultValue) {
  return value ?? defaultValue;
}
